import { useCallback, useState } from 'react'

import { useLocation, useNavigate } from 'react-router-dom'

import { useCustomSearchParams } from 'src/Hooks/useCustomSearchParams'
import { useFirstChosenTrack } from 'src/Hooks/useFirstChosenTrack'
import { useAppDispatch, useAppSelector } from 'src/Hooks/redux'
import { useSourceBusterEvent } from 'src/Hooks/useSourceBusterEvent'
import { useViewSignup } from 'src/Pages/Artist/CreateCampaignPage/NoAuthPages/ArtistRegisterPage/useViewSignup'
import { getCookieByName, getUtmParameters } from 'src/Constants/functions'
import { ANY_LANGUAGE_ID } from 'src/Constants/constants'
import {
  fillingRegisterFormEvent,
  locationEvent,
  pageViewEvent,
} from 'src/Helpers/TagManager'
import { sendFirstCampaignCreatedEvents } from 'src/Helpers/TagManager/CampaignsEvents'
import {
  registerArtist,
  RegisterArtistError,
  RegisterArtistResponse,
} from 'src/Redux/auth-process/credentialsSlice/api-actions'
import { setAccessToken } from 'src/Redux/auth-process/credentialsSlice'
import { AppDispatch } from 'src/Redux/types'
import {
  CreatePreReleaseSpotifyCampaignBody,
  CreateSpotifyCampaignBody,
  useCreatePreReleaseSpotifyCampaignMutation,
  useCreateSpotifyCampaignMutation,
  useUpdateSpotifyCampaignGenresMutation,
  useUpdateSpotifyCampaignLanguagesMutation,
} from 'src/Redux/spotify-campaignApi'
import {
  AddNewTikTokCampaignBody,
  useCreateNewTiktokCampaignMutation,
} from 'src/Redux/createNewCampaignApi'
import { SpotifyTrack } from 'src/Redux/spotifyTracksApi'
import { TiktokTrack } from 'src/Redux/firstTrackApi'
import { pageInfo } from 'src/Helpers/TagManager/pageInfo'
import { CampaignPlatformTypeEnum } from 'src/Types/campaigns'
import { FormDataProps } from 'src/Components/ArtistContactDetailsForm'
import { createdFirst } from 'src/Router/routes'
import { ArtistRoutesEnum } from 'src/Router/ArtistComponentsNewUI'
import { signupLoginError } from 'src/Helpers/TagManager/AuthEvents'
import { getLoading } from 'src/Redux/auth-process/credentialsSlice/selectors'
import { ArtistRolesEnum } from 'src/Constants/enums'
import { initiateGoogleAuth } from 'src/Utils/googleAuth'
import { PendingCampaignData } from 'src/Utils/pendingCampaignData'
import { persistPendingReferralCodes } from 'src/Utils/pendingReferralCodes'
import { buildPreReleaseCreatedUrl } from 'src/Pages/Artist/SuccessCreatedFirstPreRelease/helpers'

import { getSpotifyArtistUrl } from '../../Components/SearchArtistSection/helpers'
import {
  PreReleaseSignUpState,
  getPreReleaseSignUpState,
} from '../preReleaseSignUpState'

export interface UseArtistRegisterPage {
  isEmailExist: boolean
  clearEmailExist: () => void
  handleSubmit: (data: FormDataProps, token: string) => Promise<void>
  onGoogleClick: () => void
  loading?: boolean
  onStartFillingForm: () => void
  preReleaseSignUpState: PreReleaseSignUpState | null
}

const HTTP_STATUS_CONFLICT = 409

type SubmitSpotify = (
  data: FormDataProps,
  token: string,
  spotifyTrack: SpotifyTrack,
) => Promise<void>

type SubmitTiktok = (
  data: FormDataProps,
  token: string,
  tiktokTrack: TiktokTrack,
) => Promise<void>

type SubmitPreRelease = (
  data: FormDataProps,
  token: string,
  signUpState: PreReleaseSignUpState,
) => Promise<void>

const getCreateSpotifyCampaignBody = (
  spotifyTrack: SpotifyTrack,
): CreateSpotifyCampaignBody => ({
  artistName: spotifyTrack.artists[0]?.name || '',
  artistSpotifyId: spotifyTrack.artistSpotifyId,
  artistUrl: spotifyTrack.artists[0]?.external_urls.spotify || '',
  trackImage: spotifyTrack.trackImage,
  trackLength: spotifyTrack.trackDuration,
  trackName: spotifyTrack.trackName,
  trackSpotifyId: spotifyTrack.id,
  trackUrl: spotifyTrack.trackHref,
})

const getCreateTiktokCampaignBody = (
  tiktokTrack: TiktokTrack,
): AddNewTikTokCampaignBody => ({
  trackUrl: tiktokTrack.trackUrl || '',
  trackTiktokId: tiktokTrack.trackTiktokId,
  trackTitle: tiktokTrack.trackTitle || '',
  trackPlayUrl: tiktokTrack.trackPlayUrl || '',
  trackCoverUrl: tiktokTrack.trackCoverUrl || '',
  trackAuthorName: tiktokTrack.trackAuthorName || '',
  initialVideoCount: tiktokTrack.initialVideoCount || 0,
  trackDuration: tiktokTrack.trackDuration || 0,
})

const getCreatePreReleaseCampaignBody = ({
  artist,
  trackName,
}: PreReleaseSignUpState): CreatePreReleaseSpotifyCampaignBody => ({
  artistName: artist.name,
  artistSpotifyId: artist.spotifyId,
  artistUrl: getSpotifyArtistUrl(artist.spotifyId),
  trackName,
})

const getGooglePendingCampaignData = (
  platform: CampaignPlatformTypeEnum | undefined,
  track: { spotifyTrack?: SpotifyTrack; tiktokTrack?: TiktokTrack },
  preReleaseSignUpState: PreReleaseSignUpState | null,
): PendingCampaignData | undefined => {
  if (preReleaseSignUpState) {
    return {
      platform: 'spotify-pre-release',
      body: getCreatePreReleaseCampaignBody(preReleaseSignUpState),
    }
  }
  if (platform === CampaignPlatformTypeEnum.SPOTIFY && track.spotifyTrack) {
    return {
      platform: 'spotify',
      body: getCreateSpotifyCampaignBody(track.spotifyTrack),
    }
  }
  if (track.tiktokTrack) {
    return {
      platform: 'tiktok',
      body: getCreateTiktokCampaignBody(track.tiktokTrack),
    }
  }
  return void 0
}

const buildCreatedFirstSuccessUrl = (
  platform: CampaignPlatformTypeEnum.SPOTIFY | CampaignPlatformTypeEnum.TIKTOK,
  token: string,
  search: URLSearchParams,
): string => {
  const scTokenParams = new URLSearchParams(search.toString())
  scTokenParams.set('sc_token', token)
  return `${createdFirst}/${platform}?${scTokenParams.toString()}`
}

const buildCampaignsListUrlWithToken = (token: string): string => {
  const params = new URLSearchParams({ sc_token: token })
  return `${ArtistRoutesEnum.CampaignsList}?${params.toString()}`
}

const sendCampaignCreatedAnalytics = (params: {
  data: Pick<FormDataProps, 'email' | 'phoneNumber'>
  userId?: string
  platform: CampaignPlatformTypeEnum.SPOTIFY | CampaignPlatformTypeEnum.TIKTOK
  campaignId: string | number
  trackId: string | number
}): void => {
  const { data, userId, platform, campaignId, trackId } = params

  if (!userId) {
    pageViewEvent({ currentPageInfo: pageInfo.CreatedFirstCampaign })
    return
  }

  sendFirstCampaignCreatedEvents({
    email: data.email,
    phoneNumber: data.phoneNumber,
    platform,
    campaignId,
    trackId,
    userId,
  })

  pageViewEvent({ currentPageInfo: pageInfo.CreatedFirstCampaign })
}

const registerArtistForCampaign = async (params: {
  data: FormDataProps
  token: string
  referralCode: string | null | undefined
  dispatch: AppDispatch
}): Promise<RegisterArtistResponse> => {
  const { data, token, referralCode, dispatch } = params

  persistPendingReferralCodes({
    internalReferralCode: referralCode || void 0,
    referralCode:
      getUtmParameters(getCookieByName('soundCampaignUtm')).ref || void 0,
  })

  const response = await dispatch(
    registerArtist({
      artistRole: ArtistRolesEnum.ARTIST,
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
      phoneNumber: data.phoneNumber || void 0,
      captchaToken: token,
    }),
  ).unwrap()

  dispatch(setAccessToken({ token: response.token }))
  return response
}

const useSubmitSpotify = (params: {
  referralCode: string | null | undefined
  search: URLSearchParams
  onRegisterError: (error: RegisterArtistError) => void
}): SubmitSpotify => {
  const { referralCode, search, onRegisterError } = params
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [createSpotifyCampaign] = useCreateSpotifyCampaignMutation()
  const [updateSpotifyCampaignGenres] = useUpdateSpotifyCampaignGenresMutation()
  const [updateSpotifyCampaignLanguages] =
    useUpdateSpotifyCampaignLanguagesMutation()
  const { sendCreateFirstCampaignSourceBusterEvent } = useSourceBusterEvent()

  return useCallback(
    async (
      data: FormDataProps,
      token: string,
      spotifyTrack: SpotifyTrack,
    ): Promise<void> => {
      try {
        const response = await registerArtistForCampaign({
          data,
          token,
          referralCode,
          dispatch,
        })

        try {
          const campaign = await createSpotifyCampaign(
            getCreateSpotifyCampaignBody(spotifyTrack),
          ).unwrap()

          void updateSpotifyCampaignGenres({
            id: String(campaign.id),
            genreIds: [],
          })
          void updateSpotifyCampaignLanguages({
            id: String(campaign.id),
            languageIds: [ANY_LANGUAGE_ID],
          })

          sendCreateFirstCampaignSourceBusterEvent(
            campaign.id,
            CampaignPlatformTypeEnum.SPOTIFY,
            response.token,
          )
          sendCampaignCreatedAnalytics({
            data,
            userId: response.userId,
            platform: CampaignPlatformTypeEnum.SPOTIFY,
            campaignId: campaign.id,
            trackId: campaign.track.id,
          })
          navigate(
            buildCreatedFirstSuccessUrl(
              CampaignPlatformTypeEnum.SPOTIFY,
              response.token,
              search,
            ),
          )
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Spotify campaign creation failed:', error)
          navigate(buildCampaignsListUrlWithToken(response.token))
        }
      } catch (error) {
        onRegisterError(error as RegisterArtistError)
      }
    },
    [
      createSpotifyCampaign,
      dispatch,
      navigate,
      onRegisterError,
      referralCode,
      search,
      sendCreateFirstCampaignSourceBusterEvent,
      updateSpotifyCampaignGenres,
      updateSpotifyCampaignLanguages,
    ],
  )
}

const useSubmitTiktok = (params: {
  referralCode: string | null | undefined
  search: URLSearchParams
  onRegisterError: (error: RegisterArtistError) => void
}): SubmitTiktok => {
  const { referralCode, search, onRegisterError } = params
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [createNewTiktokCampaign] = useCreateNewTiktokCampaignMutation()
  const { sendCreateFirstCampaignSourceBusterEvent } = useSourceBusterEvent()

  return useCallback(
    async (
      data: FormDataProps,
      token: string,
      tiktokTrack: TiktokTrack,
    ): Promise<void> => {
      try {
        const response = await registerArtistForCampaign({
          data,
          token,
          referralCode,
          dispatch,
        })

        try {
          const campaign = await createNewTiktokCampaign(
            getCreateTiktokCampaignBody(tiktokTrack),
          ).unwrap()

          sendCreateFirstCampaignSourceBusterEvent(
            Number(campaign.id),
            CampaignPlatformTypeEnum.TIKTOK,
            response.token,
          )
          sendCampaignCreatedAnalytics({
            data,
            userId: response.userId,
            platform: CampaignPlatformTypeEnum.TIKTOK,
            campaignId: campaign.id,
            trackId: campaign.trackId,
          })
          navigate(
            buildCreatedFirstSuccessUrl(
              CampaignPlatformTypeEnum.TIKTOK,
              response.token,
              search,
            ),
          )
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('TikTok campaign creation failed:', error)
          navigate(buildCampaignsListUrlWithToken(response.token))
        }
      } catch (error) {
        onRegisterError(error as RegisterArtistError)
      }
    },
    [
      createNewTiktokCampaign,
      dispatch,
      navigate,
      onRegisterError,
      referralCode,
      search,
      sendCreateFirstCampaignSourceBusterEvent,
    ],
  )
}

const useSubmitPreRelease = (params: {
  referralCode: string | null | undefined
  search: URLSearchParams
  onRegisterError: (error: RegisterArtistError) => void
}): SubmitPreRelease => {
  const { referralCode, search, onRegisterError } = params
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [createPreReleaseSpotifyCampaign] =
    useCreatePreReleaseSpotifyCampaignMutation()
  const [updateSpotifyCampaignGenres] = useUpdateSpotifyCampaignGenresMutation()
  const [updateSpotifyCampaignLanguages] =
    useUpdateSpotifyCampaignLanguagesMutation()
  const { sendCreateFirstCampaignSourceBusterEvent } = useSourceBusterEvent()

  return useCallback(
    async (
      data: FormDataProps,
      token: string,
      signUpState: PreReleaseSignUpState,
    ): Promise<void> => {
      try {
        const response = await registerArtistForCampaign({
          data,
          token,
          referralCode,
          dispatch,
        })

        try {
          const campaign = await createPreReleaseSpotifyCampaign(
            getCreatePreReleaseCampaignBody(signUpState),
          ).unwrap()

          void updateSpotifyCampaignGenres({
            id: String(campaign.id),
            genreIds: [],
          })
          void updateSpotifyCampaignLanguages({
            id: String(campaign.id),
            languageIds: [ANY_LANGUAGE_ID],
          })

          sendCreateFirstCampaignSourceBusterEvent(
            campaign.id,
            CampaignPlatformTypeEnum.SPOTIFY,
            response.token,
          )
          sendCampaignCreatedAnalytics({
            data,
            userId: response.userId,
            platform: CampaignPlatformTypeEnum.SPOTIFY,
            campaignId: campaign.id,
            trackId: campaign.track.id,
          })
          navigate(
            buildPreReleaseCreatedUrl({
              campaignId: campaign.id,
              status: campaign.status,
              token: response.token,
              search,
            }),
          )
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Pre-release campaign creation failed:', error)
          navigate(buildCampaignsListUrlWithToken(response.token))
        }
      } catch (error) {
        onRegisterError(error as RegisterArtistError)
      }
    },
    [
      createPreReleaseSpotifyCampaign,
      dispatch,
      navigate,
      onRegisterError,
      referralCode,
      search,
      sendCreateFirstCampaignSourceBusterEvent,
      updateSpotifyCampaignGenres,
      updateSpotifyCampaignLanguages,
    ],
  )
}

export const useArtistRegisterPage = (): UseArtistRegisterPage => {
  const { state } = useLocation()
  const { search } = useCustomSearchParams()
  const artistSignupLoading = useAppSelector(getLoading)

  const [isEmailExist, setIsEmailExist] = useState(false)

  const preReleaseSignUpState = getPreReleaseSignUpState(state)
  const { track, platform, loading } = useFirstChosenTrack()
  const referralCode = search?.get('referralCode')

  useViewSignup(
    preReleaseSignUpState ? CampaignPlatformTypeEnum.SPOTIFY : void 0,
  )

  const onStartFillingForm = (): void => {
    locationEvent()
    fillingRegisterFormEvent()
  }

  const handleRegisterArtistError = useCallback(
    (error: RegisterArtistError): void => {
      signupLoginError()

      if (error.status === HTTP_STATUS_CONFLICT) {
        setIsEmailExist(true)
      }
    },
    [],
  )

  const handleSubmitSpotify = useSubmitSpotify({
    referralCode,
    search,
    onRegisterError: handleRegisterArtistError,
  })
  const handleSubmitTiktok = useSubmitTiktok({
    referralCode,
    search,
    onRegisterError: handleRegisterArtistError,
  })
  const handleSubmitPreRelease = useSubmitPreRelease({
    referralCode,
    search,
    onRegisterError: handleRegisterArtistError,
  })

  const handleSubmit = useCallback(
    async (data: FormDataProps, token: string): Promise<void> => {
      setIsEmailExist(false)

      if (preReleaseSignUpState) {
        await handleSubmitPreRelease(data, token, preReleaseSignUpState)
        return
      }

      if (platform === CampaignPlatformTypeEnum.SPOTIFY && track.spotifyTrack) {
        await handleSubmitSpotify(data, token, track.spotifyTrack)
        return
      }

      if (track.tiktokTrack) {
        await handleSubmitTiktok(data, token, track.tiktokTrack)
      }
    },
    [
      platform,
      track,
      preReleaseSignUpState,
      handleSubmitPreRelease,
      handleSubmitSpotify,
      handleSubmitTiktok,
    ],
  )

  const onGoogleClick = useCallback((): void => {
    const utmObj = getUtmParameters(getCookieByName('soundCampaignUtm'))

    initiateGoogleAuth({
      internalReferralCode: referralCode || void 0,
      referralCode: utmObj.ref || void 0,
      campaignData: getGooglePendingCampaignData(
        platform,
        track,
        preReleaseSignUpState,
      ),
      isSignUp: true,
    })
  }, [platform, track, preReleaseSignUpState, referralCode])

  const clearEmailExist = useCallback((): void => {
    setIsEmailExist(false)
  }, [])

  return {
    isEmailExist,
    clearEmailExist,
    handleSubmit,
    onGoogleClick,
    onStartFillingForm,
    preReleaseSignUpState,
    loading: artistSignupLoading || loading,
  }
}
